import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, uploadFile } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import './eventFiles.css';

const EVENTS_RESOURCE = 'events.events';
const FILES_RESOURCE = 'files.files';
/** The server's limit of a description (modules/events event-files.ts). */
const DESCRIPTION_MAX = 300;

export type EventFile = {
  id: string;
  fileId: string;
  name: string;
  size: number;
  mimeType: string;
  description: string;
  addedBy: string;
  addedByName: string;
  addedAt: string;
  version: number;
};

export const eventFilesKey = (eventId: string) => ['event-files', eventId] as const;

/** „1,2 MB”, „340 kB”, „12 B” (1 MB = 1024 kB, as the 25 MB limit counts). */
export function sizeText(bytes: number): string {
  const fmt = (n: number) => n.toLocaleString('pl-PL', { maximumFractionDigits: 1 });
  if (bytes >= 1024 * 1024) return `${fmt(bytes / (1024 * 1024))} MB`;
  if (bytes >= 1024) return `${fmt(Math.round(bytes / 1024))} kB`;
  return `${bytes} B`;
}

const DAY = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric', year: 'numeric' });

/**
 * „Pliki” in the event card's details (owner 2026-11-04 18:47: „chce przypinac pliki np. rider techniczny do
 * wydarzenia”): files kept by the files module (on the server, in backups), each with a short description. Hidden
 * without „Pliki i załączniki: Podgląd”; adding needs editing the event and „Dodawanie”, describing and removing editing.
 */
export function EventFiles({ eventId }: { eventId: string }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const mayView = !!me && can(me, EVENTS_RESOURCE, 'view') && can(me, FILES_RESOURCE, 'view');
  const list = useQuery({ queryKey: eventFilesKey(eventId), enabled: mayView, queryFn: () => api<{ files: EventFile[] }>(`/api/events/${eventId}/files`) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: eventFilesKey(eventId) });

  const [adding, setAdding] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);

  const add = useMutation({
    mutationFn: async (input: { file: File; description: string }) => {
      const fileId = await uploadFile(input.file);
      return api<EventFile>(`/api/events/${eventId}/files`, { method: 'POST', body: { fileId, description: input.description } });
    },
    onSuccess: async () => {
      setAdding(false);
      setFile(null);
      setDescription('');
      await refresh();
    },
  });
  const describe = useMutation({
    mutationFn: (input: { f: EventFile; text: string }) => api<EventFile>(`/api/events/${eventId}/files/${input.f.id}`, { method: 'PATCH', body: { description: input.text, version: input.f.version } }),
    onSuccess: async () => {
      setEditing(null);
      remove.reset();
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (f: EventFile) => api<void>(`/api/events/${eventId}/files/${f.id}?version=${f.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      // review M4: an older failed description edit is no news any more
      describe.reset();
      await refresh();
    },
  });

  if (!me || !mayView) return null;
  const mayEdit = can(me, EVENTS_RESOURCE, 'edit');
  const mayAdd = mayEdit && can(me, FILES_RESOURCE, 'create');
  const files = list.data?.files;
  const writeError = [describe.error, remove.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const addError = add.error instanceof ApiError ? add.error : add.error ? new ApiError(0, 'unknown', 'Nie udało się dodać pliku') : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (file) add.mutate({ file, description: description.trim() });
  }

  return (
    <section className="event-files" aria-label="Pliki" data-feature-id="events.files">
      <div className="event-files-head">
        <h3>Pliki</h3>
        {mayAdd && !adding && (
          <button className="btn small ghost" type="button" onClick={() => setAdding(true)}>
            + Dodaj plik
          </button>
        )}
      </div>
      {list.isLoading && <p className="muted small" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać plików'}</p>}
      {writeError?.status === 409 && (
        <ConflictNotice
          onReload={() => {
            describe.reset();
            remove.reset();
            setEditing(null);
            void refresh();
          }}
        />
      )}
      {writeError && writeError.status !== 409 && <p className="alert" role="alert">{writeError.message}</p>}
      {files && files.length === 0 && !adding && <p className="muted small">Brak plików. Przypnij tu np. rider techniczny albo plan sceny.</p>}
      {files && files.length > 0 && (
        <ul className="event-files-list">
          {files.map((f) => (
            <li key={f.id}>
              {editing?.id === f.id ? (
                <form
                  className="event-files-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    describe.mutate({ f, text: editing.text.trim() });
                  }}
                >
                  <label className="field">
                    <span>Opis pliku {f.name}</span>
                    <input type="text" maxLength={DESCRIPTION_MAX} value={editing.text} autoFocus onChange={(e) => setEditing({ id: f.id, text: e.target.value })} />
                  </label>
                  <div className="actions">
                    <button className="btn small primary" type="submit" disabled={describe.isPending}>
                      Zapisz
                    </button>
                    <button className="btn small" type="button" onClick={() => setEditing(null)}>
                      Anuluj
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <span className="event-file-main">
                    <a href={`/api/files/${f.fileId}`} download={f.name}>
                      {f.name}
                    </a>
                    {f.description && <span className="event-file-description">{f.description}</span>}
                    <span className="event-file-meta">{[sizeText(f.size), f.addedByName, DAY.format(new Date(f.addedAt))].filter(Boolean).join(' · ')}</span>
                  </span>
                  {mayEdit && (
                    <span className="event-file-actions">
                      <button className="btn small ghost" type="button" aria-label={`Zmień opis: ${f.name}`} onClick={() => setEditing({ id: f.id, text: f.description })}>
                        Zmień opis
                      </button>
                      <button
                        className="btn small ghost"
                        type="button"
                        aria-label={`Usuń plik: ${f.name}`}
                        disabled={remove.isPending}
                        onClick={() => window.confirm(`Usunąć plik „${f.name}” z wydarzenia?`) && remove.mutate(f)}
                      >
                        Usuń
                      </button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {adding && (
        <form className="event-files-form" onSubmit={submit}>
          <label className="field">
            <span>Plik</span>
            <input type="file" disabled={add.isPending} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          <label className="field">
            <span>Opis (nieobowiązkowy)</span>
            <input type="text" maxLength={DESCRIPTION_MAX} value={description} placeholder="np. rider techniczny" onChange={(e) => setDescription(e.target.value)} />
          </label>
          <div className="actions">
            <button className="btn small primary" type="submit" disabled={!file || add.isPending}>
              {add.isPending ? 'Wgrywanie…' : 'Dodaj'}
            </button>
            <button
              className="btn small"
              type="button"
              onClick={() => {
                setAdding(false);
                setFile(null);
                setDescription('');
                add.reset();
              }}
            >
              Anuluj
            </button>
          </div>
          {addError && <p className="alert" role="alert">{addError.message}</p>}
          <p className="hint">Dozwolone: pdf, jpg, png, heic, webp, docx, xlsx, csv, txt (do 25 MB).</p>
        </form>
      )}
    </section>
  );
}
